import { useEffect, useState } from "react";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface FloatingTableOfContentsProps {
  items: TocItem[];
}

/**
 * Desktop-only floating/sticky table of contents.
 * On mobile, use TableOfContents component inline instead.
 */
export function FloatingTableOfContents({ items }: FloatingTableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? "");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      {
        rootMargin: "-80px 0px -60% 0px",
        threshold: 0,
      },
    );

    for (const item of items) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [items]);

  // Show the floating TOC once user scrolls past the hero
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Table of contents"
      className={`fixed right-6 top-24 z-40 hidden w-56 transition-all duration-300 xl:block ${
        isVisible
          ? "translate-x-0 opacity-100"
          : "translate-x-8 opacity-0 pointer-events-none"
      }`}
    >
      <div className="rounded-md border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          Contents
        </p>
        <ul className="mt-3 space-y-1">
          {items.map((item) => {
            const isActive = activeId === item.id;

            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(item.id);
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "start" });
                      setActiveId(item.id);
                    }
                  }}
                  className={`block rounded-md px-2.5 py-1.5 text-[13px] transition ${
                    isActive
                      ? "bg-sky-50 font-semibold text-sky-700"
                      : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {item.text}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}